import { ArrowLeft, Bell, Calendar, ChevronDown, ChevronUp, Copy, Download, ExternalLink, RefreshCw, RotateCw, ThumbsDown, ThumbsUp } from "lucide-react";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";


import Tabs from "../../lib/ui/components/tabs/Tabs";
import { toast } from "sonner";

import { copyToClipboardHandler, exportToPDFHandler } from "./ExportAnalysisHelper";

import Button from "../../lib/ui/components/button/Button";
import Modal from "../../lib/ui/components/Modal";
import { Heading6, Heading4, BodyText } from "../../lib/ui/components/typography/Typography";
import MatchRating from "../../lib/ui/components/match-rating/MatchRating";
import Badge from "../../lib/ui/components/badge/Badge";
import Spinner from "../../lib/ui/components/spinner/Spinner";

import { formatStatus, getDisplayStatus } from "./StatusHelper";
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { useStore } from "../../store";
import { isDevEnvironment } from "../../utils/envUtils";
import {
    RETRY_ANALYSIS,
    FETCH_CANDIDATE_INTERVIEWS,
    DEV_SEND_INTERVIEW_MESSAGE,
    UPDATE_INTERVIEW_ANALYSIS_FEEDBACK,
    SEND_CALENDAR_LINK,
    SEND_CALENDAR_REMINDER,
    REJECT_CANDIDATES,
    RESEND_INTERVIEW,
    CLOSE_INTERVIEW,
    REOPEN_INTERVIEW,
} from "../../lib/urls";
import RejectApplicantModal from "./RejectApplicantModal";
import Tooltip from "../../lib/ui/components/tooltip/Tooltip";
import Dropdown from "../../lib/ui/components/dropdown/Dropdown";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import AnalysisRubricRequiredModal from "../ConfirmationModal/AnalysisRubricRequiredModal";
import { getModalConfig } from "../ConfirmationModal/modalHelper";
import { isRejectSupported } from "../../lib/constants";
import { isAnalysisRubricRequiredDetail } from "../../utils/analysisRubricPreflight";
import "./ApplicantAnalysisResult.css";

import CollapsibleContent from "../../lib/ui/components/collapsible-content/CollapsibleContent";
import CollapsibleSection from "../../lib/ui/components/collapsible-section/CollapsibleSection";
import { useAnalysisTimer } from "../../hooks/useAnalysisTimer";
import ErrorBoundary from "../ErrorBoundary/ErrorBoundary";
import ResumeTabContent from "./ResumeTabContent";
import {
    canRetryAnalysis,
    canRetryMoveAtsStages,
    formatRetryAnalysisToast,
    getApiErrorMessage,
    getAtsStageUpdateDisabledReason,
    getRetryAnalysisDisabledReason,
    isAnalysisRetryEligible,
    renderRetryProgressToast,
    waitForRetryAnalysesToFinish,
} from "../../utils/retryAnalysisUtils";
import MultipleApplicationsModal from "./MultipleApplicationsModal";
import { atsApplicationCount, isSafeExternalUrl } from "./atsApplications";
import OutdatedQuestionsBadge from "../../lib/ui/components/outdated-questions-badge/OutdatedQuestionsBadge";
import {
    getCandidateResponseLabel,
    getOverallRatingDisplay,
    getQuestionScoreDisplay,
    getRequiredQuestionsLabel as getPassFailLabel,
    getRequiredQuestionsVariant as getPassFailVariant,
    getResumeMatchDisplay,
    getReviewReasons,
    getScoringRubricEntries,
    isReviewRequired,
} from "../../utils/analysisDisplay";

const QuestionCard = ({ question, index, isExpanded, onToggle, aiInsights }) => {
    const scoreDisplay = getQuestionScoreDisplay(question);

    return (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <BodyText weight="bold" emphasis={false} className="min-w-0 flex-1">
                    {index + 1}: {question.interview_question}
                </BodyText>
                <div className="flex shrink-0 items-center gap-4 whitespace-nowrap sm:ml-4">
                    <BodyText weight="bold" emphasis={false} className="whitespace-nowrap">
                        {question?.is_pass_fail ? "Result" : "Match Rating"}
                    </BodyText>
                    {scoreDisplay.kind === "rating" ? (
                        <MatchRating rating={scoreDisplay.value} />
                    ) : (
                        <BodyText weight="normal" emphasis={false} className="whitespace-nowrap" data-testid={`question-score-text-${index}`}>
                            {scoreDisplay.value}
                        </BodyText>
                    )}
                </div>
            </div>

            <hr className="border-gray-200 mb-4" />

            <div className="mb-4">
                <BodyText weight="bold" emphasis={false} className="mb-2">
                    Evaluation
                </BodyText>
                <BodyText weight="normal" emphasis={false} className="text-gray-700">{question?.justification}</BodyText>
            </div>

            <Button
                variant="secondary"
                onClick={() => onToggle(index)}
                rightIcon={isExpanded ? <ChevronUp size="1em" /> : <ChevronDown size="1em" />}
            >
                {isExpanded ? 'Hide Details' : 'Show Details'}
            </Button>

            <CollapsibleContent isOpen={isExpanded}>
                <div className="mt-4 space-y-4 pt-4 border-t border-gray-100">
                    <div>
                        <BodyText weight="bold" emphasis={false} className="mb-2">
                            {getCandidateResponseLabel(aiInsights)}
                        </BodyText>
                        <BodyText weight="normal" emphasis={false} className="mb-2">
                            {question?.candidate_response
                                ? `"${question.candidate_response}"`
                                : "No response recorded."}
                        </BodyText>
                    </div>

                    <div>
                        <BodyText weight="bold" emphasis={false} className="mb-2">
                            {question?.scoring_rubric ? "Scoring Rubric" : "Legacy Desired Response"}
                        </BodyText>
                        <BodyText weight="normal" emphasis={false} className="mb-2">
                            {question?.scoring_rubric?.levels
                                ? getScoringRubricEntries(question).map(({ level, label, description }) => (
                                    <span key={level} className="block"><strong>{label}:</strong> {description}</span>
                                ))
                                : question?.desired_answer}
                        </BodyText>
                    </div>
                </div>
            </CollapsibleContent>
        </div>
    );
};

const ApplicantAnalysisResult = ({
    selectedApplicantForAnalysis,
    navigateBackToList,
    onDataUpdate,
    isUpdating: parentIsUpdating = false,
    roleName = "",
    companyName = "",
    companyId,
    jobId,
    token,
    onAnalysisFeedbackSaved,
    /** When `onDataUpdate` is absent (e.g. applicant search), patch local state after a successful send. */
    onCalendarLinkSent,
    isTranscriptLoading = false,
    transcriptError = null,
    onRetryTranscript,
    jobStageSettings = null,
    hasAnalysisRubric,
    onAnalysisRubricRequired,
    /** `drawer` hides list-back chrome and stacks for a right-side panel. */
    presentation = "page",
    onOpenResumePreview,
    /** Tab index to open on mount (0 Resume, 1 Evaluation Details, 2 Transcript). */
    initialAnalysisTab = 0,
    onAnalysisTabChange,
}) => {
    const isDrawer = presentation === "drawer";
    const navigate = useNavigate();
    const [expandedQuestions, setExpandedQuestions] = useState(new Set());
    const [isRetrying, setIsRetrying] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [devMessageBody, setDevMessageBody] = useState("");
    const [isSendingDevMessage, setIsSendingDevMessage] = useState(false);
    const devModeEnabled = useStore((state) => state.devModeEnabled);
    const company = useStore((state) => state.company);
    const rejectSupported = isRejectSupported(company?.ats_integration_id);
    const [isAnalysisLiked, setIsAnalysisLiked] = useState(null);
    const [dislikeModalOpen, setDislikeModalOpen] = useState(false);
    const [dislikeCommentDraft, setDislikeCommentDraft] = useState("");
    const [isSavingFeedback, setIsSavingFeedback] = useState(false);
    const [isSendingCalendarLink, setIsSendingCalendarLink] = useState(false);
    const [isSendingReminder, setIsSendingReminder] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [applicationsModalOpen, setApplicationsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resendInterviewModalOpen, setResendInterviewModalOpen] = useState(false);
  const [analysisRubricWarningOpen, setAnalysisRubricWarningOpen] = useState(false);
  const [interviewActionModalOpen, setInterviewActionModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [retryAnalysisModalOpen, setRetryAnalysisModalOpen] = useState(false);
  // Lazy-mount gate: the Resume tab fetches the PDF and can trigger paid
  // OpenAI generation, so only mount its content when the tab is active.
  const RESUME_TAB_INDEX = 0;
  const [activeAnalysisTab, setActiveAnalysisTab] = useState(initialAnalysisTab);
  const handleAnalysisTabChange = useCallback(
    (tabIndex) => {
      setActiveAnalysisTab(tabIndex);
      onAnalysisTabChange?.(tabIndex);
    },
    [onAnalysisTabChange]
  );

  const isJobClosed =
    selectedApplicantForAnalysis?.job?.status != null &&
    selectedApplicantForAnalysis.job.status !== "OPEN";

  const showAnalysisRubricWarning = useCallback(() => {
    if (onAnalysisRubricRequired) {
      onAnalysisRubricRequired();
      return;
    }
    setAnalysisRubricWarningOpen(true);
  }, [onAnalysisRubricRequired]);

  const overflowMenuItems = useMemo(() => {
    const applicant = selectedApplicantForAnalysis;
    if (!applicant) return [];

    const hasInterviewSession = Boolean(applicant.interview_session_id);
    const followUpDisabled =
      !applicant.interview_session_id ||
      !(
        applicant.status === "PENDING" || applicant.status === "IN_PROGRESS"
      );

    const items = [];

    if (hasInterviewSession) {
      items.push({
        label: "Resend Interview",
        onClick: () => {
          if (hasAnalysisRubric === false) {
            showAnalysisRubricWarning();
            return;
          }
          setResendInterviewModalOpen(true);
        },
        disabled: isJobClosed,
        variant: "normal",
      });
    }

    if (applicant.is_closed) {
      items.push({
        label: "Enable Follow-up Messages",
        onClick: () => setInterviewActionModalOpen(true),
        disabled: !applicant.interview_session_id,
        variant: "normal",
      });
    } else {
      items.push({
        label: "Disable Follow-up Messages",
        onClick: () => setInterviewActionModalOpen(true),
        disabled: followUpDisabled,
        variant: "danger",
      });
    }

    if (isAnalysisRetryEligible(applicant, { isJobClosed: false })) {
      const retryDisabled = !canRetryAnalysis(applicant, { isJobClosed, job: jobStageSettings });
      items.push({
        label: "Retry Evaluation",
        onClick: () => setRetryAnalysisModalOpen(true),
        disabled: retryDisabled,
        title: retryDisabled
          ? getRetryAnalysisDisabledReason(applicant, { isJobClosed, job: jobStageSettings })
          : undefined,
        variant: "normal",
      });
    }

    const applicationCount = atsApplicationCount(applicant);
    if (applicationCount > 1) {
      items.push({
        label: `Multiple Applications (${applicationCount})`,
        onClick: () => setApplicationsModalOpen(true),
        variant: "normal",
      });
    }

    items.push({
      label: "Delete",
      onClick: () => setDeleteModalOpen(true),
      variant: "danger",
    });

    return items;
  }, [
    selectedApplicantForAnalysis,
    isJobClosed,
    company,
    jobStageSettings,
    hasAnalysisRubric,
    showAnalysisRubricWarning,
  ]);

    const canSendCalendarLink = useMemo(() => {
        const a = selectedApplicantForAnalysis;
        const interviewStatus = String(a?.status || "").toUpperCase();
        const analysisStatus = String(a?.analysis_status || "").toUpperCase();
        const interviewComplete =
            interviewStatus === "COMPLETED" || interviewStatus === "FEEDBACK";
        const analysisComplete =
            analysisStatus === "COMPLETED" || Boolean(a?.ai_insights);

        return interviewComplete && analysisComplete && Boolean(a?.id);
    }, [
        selectedApplicantForAnalysis?.status,
        selectedApplicantForAnalysis?.analysis_status,
        selectedApplicantForAnalysis?.ai_insights,
        selectedApplicantForAnalysis?.id,
    ]);

    const calendarLinkButtonLabel = useMemo(() => {
        if (isSendingCalendarLink) return "Sending...";
        return selectedApplicantForAnalysis?.calendar_link_sent_at
            ? "Resend Calendar Link"
            : "Send Calendar Link";
    }, [
        isSendingCalendarLink,
        selectedApplicantForAnalysis?.calendar_link_sent_at,
    ]);

    useEffect(() => {
        const liked = selectedApplicantForAnalysis?.ai_insights?.analysis_liked;
        if (liked === true || liked === false) {
            setIsAnalysisLiked(liked);
        } else {
            setIsAnalysisLiked(null);
        }
    }, [
        selectedApplicantForAnalysis?.interview_session_id,
        selectedApplicantForAnalysis?.ai_insights?.analysis_liked,
    ]);

    const handleUpdate = async () => {
        setIsUpdating(true);
        try {
            if (onDataUpdate) {
                await onDataUpdate();
            }
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }
            console.error("Applicant analysis result update error:", error);
            toast.error("Update failed, please try again later");
        } finally {
            setIsUpdating(false);
        }
    };

    const { shouldShowRetryButton, initiateRetry, forceUpdate } = useAnalysisTimer(selectedApplicantForAnalysis);


    const toggleQuestion = (index) => {
        const newExpanded = new Set(expandedQuestions);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedQuestions(newExpanded);
    };

    const retryAnalysisHandler = async (moveAtsStages) => {
        setIsRetrying(true);
        setRetryAnalysisModalOpen(false);
        const loadingToastId = toast.loading(renderRetryProgressToast(0, 1));
        const sessionId = selectedApplicantForAnalysis.interview_session_id;

        try {
            const response = await fetch(
                RETRY_ANALYSIS(companyId, jobId, sessionId),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ move_ats_stages: Boolean(moveAtsStages) }),
                },
            );

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(data, "Failed to run evaluation"));
            }

            const data = await response.json();
            initiateRetry();

            const summary = await waitForRetryAnalysesToFinish({
                sessionIds: [sessionId],
                fetchApplicants: async () => {
                    if (onDataUpdate) {
                        const result = await onDataUpdate();
                        if (Array.isArray(result)) {
                            return result;
                        }
                    }
                    if (!companyId || !jobId || !token) {
                        return [];
                    }
                    const listResponse = await fetch(
                        FETCH_CANDIDATE_INTERVIEWS(companyId, jobId),
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    );
                    if (!listResponse.ok) {
                        return [];
                    }
                    const listData = await listResponse.json();
                    return Array.isArray(listData) ? listData : [];
                },
                onProgress: ({ completed }) => {
                    toast.loading(renderRetryProgressToast(completed, 1), {
                        id: loadingToastId,
                    });
                },
            });

            if (summary.failed === 0 && summary.completed === 1) {
                toast.success(
                    formatRetryAnalysisToast({
                        move_ats_stages: Boolean(moveAtsStages),
                        ats_stage_skip_reason: data?.ats_stage_skip_reason,
                    }),
                    { id: loadingToastId }
                );
            } else {
                toast.error("Evaluation failed.", { id: loadingToastId });
            }
        } catch (error) {
            console.error("Error during analysis:", error);
            toast.error(error.message || "Failed to run evaluation", {
                id: loadingToastId,
            });
        } finally {
            setIsRetrying(false);
            setIsUpdating(false);
        }
    };

    const handleSendDevMessage = async () => {
        const body = devMessageBody?.trim();
        const phone = selectedApplicantForAnalysis?.phone;
        if (!body || !phone) {
            toast.error("Enter a message and ensure the applicant has a phone number.");
            return;
        }
        setIsSendingDevMessage(true);
        try {
            const response = await fetch(DEV_SEND_INTERVIEW_MESSAGE, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ phone, body }),
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.detail || "Failed to send test message");
            }
            toast.success("Test message sent");
            setDevMessageBody("");
            if (onDataUpdate) await onDataUpdate();
        } catch (error) {
            console.error("Dev send interview message error:", error);
            toast.error(error.message || "Failed to send test message");
        } finally {
            setIsSendingDevMessage(false);
        }
    };

    const submitAnalysisFeedback = useCallback(
        async (liked, comment) => {
            const sessionId = selectedApplicantForAnalysis?.interview_session_id;
            if (!companyId || !jobId || !token || !sessionId) {
                toast.error("Unable to save feedback. Try refreshing the page.");
                return;
            }
            setIsSavingFeedback(true);
            try {
                const url = UPDATE_INTERVIEW_ANALYSIS_FEEDBACK(
                    companyId,
                    jobId,
                    sessionId
                );
                const res = await fetch(url, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        analysis_liked: liked,
                        analysis_comment: comment ?? null,
                    }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok) {
                    const detail = data.detail;
                    const message =
                        typeof detail === "string"
                            ? detail
                            : Array.isArray(detail)
                              ? detail.map((d) => d.msg || d).join(" ")
                              : "Failed to save feedback";
                    throw new Error(message);
                }
                setIsAnalysisLiked(liked);
                setDislikeModalOpen(false);
                toast.success("Thanks for your feedback");
                if (onAnalysisFeedbackSaved) {
                    onAnalysisFeedbackSaved(data);
                }
                if (onDataUpdate) {
                    await onDataUpdate();
                }
            } catch (error) {
                console.error("Analysis feedback error:", error);
                toast.error(error.message || "Failed to save feedback");
                const prev = selectedApplicantForAnalysis?.ai_insights?.analysis_liked;
                setIsAnalysisLiked(
                    prev === true || prev === false ? prev : null
                );
            } finally {
                setIsSavingFeedback(false);
            }
        },
        [
            companyId,
            jobId,
            token,
            selectedApplicantForAnalysis?.interview_session_id,
            selectedApplicantForAnalysis?.ai_insights?.analysis_liked,
            onDataUpdate,
            onAnalysisFeedbackSaved,
        ]
    );

    const handleAnalysisLike = async (liked) => {
        if (liked) {
            await submitAnalysisFeedback(true, null);
            return;
        }
        setDislikeCommentDraft(
            selectedApplicantForAnalysis?.ai_insights?.analysis_comment || ""
        );
        setDislikeModalOpen(true);
    };

    const confirmDislikeFeedback = async () => {
        const trimmed = dislikeCommentDraft.trim();
        await submitAnalysisFeedback(false, trimmed || null);
    };

    const sendCalendarLinkFromDetail = async () => {
        const applicant = selectedApplicantForAnalysis;
        if (!applicant?.id) {
            toast.error("Invalid applicant data");
            return;
        }
        if (!companyId || !jobId || !token) {
            toast.error("Unable to send calendar link. Try refreshing the page.");
            return;
        }
        if (!canSendCalendarLink) {
            return;
        }

        setIsSendingCalendarLink(true);
        try {
            const url = SEND_CALENDAR_LINK(companyId, jobId);
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    candidate_ids: [applicant.id],
                }),
            });

            if (response.ok) {
                toast.success(`Calendar link sent to ${applicant.name}`);
                const sentAt = new Date().toISOString();
                try {
                    if (onDataUpdate) {
                        await onDataUpdate();
                    } else if (onCalendarLinkSent) {
                        onCalendarLinkSent(sentAt);
                    }
                } catch (refreshErr) {
                    if (refreshErr?.name === "AbortError") {
                        return;
                    }
                    console.error("Error refreshing after calendar send:", refreshErr);
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                const detail = errorData.detail;
                const message =
                    typeof detail === "string"
                        ? detail
                        : Array.isArray(detail)
                          ? detail.map((d) => d.msg || d).join(" ")
                          : "Failed to send calendar link";
                throw new Error(message);
            }
        } catch (error) {
            console.error("Error sending calendar link from analysis:", error);
            toast.error(error.message || "Failed to send calendar link");
        } finally {
            setIsSendingCalendarLink(false);
        }
    };

    const sendReminderFromDetail = async () => {
        const applicant = selectedApplicantForAnalysis;
        if (!applicant?.id) {
            toast.error("Invalid applicant data");
            return;
        }
        if (!companyId || !jobId || !token) {
            toast.error("Unable to send reminder. Try refreshing the page.");
            return;
        }
        if (!canSendCalendarLink) {
            return;
        }

        setIsSendingReminder(true);
        try {
            const url = SEND_CALENDAR_REMINDER(companyId, jobId);
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    candidate_ids: [applicant.id],
                }),
            });

            if (response.ok) {
                toast.success(`Reminder sent to ${applicant.name}`);
            } else {
                const errorData = await response.json().catch(() => ({}));
                const detail = errorData.detail;
                const message =
                    typeof detail === "string"
                        ? detail
                        : Array.isArray(detail)
                          ? detail.map((d) => d.msg || d).join(" ")
                          : "Failed to send reminder";
                throw new Error(message);
            }
        } catch (error) {
            console.error("Error sending reminder from analysis:", error);
            toast.error(error.message || "Failed to send reminder");
        } finally {
            setIsSendingReminder(false);
        }
    };

    const confirmResendInterview = async () => {
        const applicant = selectedApplicantForAnalysis;
        if (!applicant?.id || !companyId || !jobId || !token) return;

        setIsActionLoading(true);
        try {
            const response = await fetch(
                RESEND_INTERVIEW(companyId, jobId, applicant.id),
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.ok) {
                setResendInterviewModalOpen(false);
                toast.success(
                    `Interview resent successfully to ${applicant.name}`
                );
                if (onDataUpdate) {
                    await onDataUpdate();
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                if (isAnalysisRubricRequiredDetail(errorData.detail)) {
                    setResendInterviewModalOpen(false);
                    showAnalysisRubricWarning();
                    return;
                }
                throw new Error(errorData.detail || "Failed to resend interview");
            }
        } catch (error) {
            console.error("Error resending interview:", error);
            toast.error(
                error.message || "Failed to resend interview. Please try again."
            );
        } finally {
            setIsActionLoading(false);
        }
    };

    const confirmInterviewFollowUpAction = async () => {
        const applicant = selectedApplicantForAnalysis;
        if (!applicant?.interview_session_id || !companyId || !jobId || !token) {
            toast.error("No interview session found for this applicant");
            return;
        }

        const isReopen = Boolean(applicant.is_closed);
        setIsActionLoading(true);
        try {
            const url = isReopen
                ? REOPEN_INTERVIEW(companyId, jobId)
                : CLOSE_INTERVIEW(companyId, jobId);

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    candidate_ids: [applicant.interview_session_id],
                }),
            });

            if (response.ok) {
                setInterviewActionModalOpen(false);
                toast.success(
                    isReopen
                        ? `Follow-up messages enabled for ${applicant.name}`
                        : `Follow-up messages disabled for ${applicant.name}`
                );
                if (onDataUpdate) {
                    await onDataUpdate();
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.detail ||
                        (isReopen
                            ? "Failed to enable follow-up messages"
                            : "Failed to disable follow-up messages")
                );
            }
        } catch (error) {
            console.error("Error updating follow-up messages:", error);
            toast.error(
                error.message ||
                    (applicant.is_closed
                        ? "Failed to enable follow-up messages"
                        : "Failed to disable follow-up messages")
            );
        } finally {
            setIsActionLoading(false);
        }
    };

    const confirmDeleteApplicant = async () => {
        const applicant = selectedApplicantForAnalysis;
        if (!applicant?.id || !companyId || !jobId || !token) return;

        setIsActionLoading(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const url = `${apiUrl}/companies/${companyId}/jobs/${jobId}/candidates/${applicant.id}`;

            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.ok) {
                setDeleteModalOpen(false);
                toast.success(
                    `Applicant ${applicant.name} has been deleted successfully.`
                );
                if (navigateBackToList) {
                    navigateBackToList();
                }
                if (onDataUpdate) {
                    await onDataUpdate();
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || "Failed to delete candidate");
            }
        } catch (error) {
            console.error("Error deleting applicant:", error);
            toast.error(error.message || "Failed to delete applicant");
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleRejectConfirm = async ({ rejection_reason_id, note }) => {
        const applicant = selectedApplicantForAnalysis;
        if (!applicant?.id) return;

        setIsRejecting(true);
        try {
            const url = REJECT_CANDIDATES(companyId, jobId);
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    candidate_ids: [applicant.id],
                    rejection_reason_id,
                    note,
                }),
            });

            if (response.ok) {
                setRejectModalOpen(false);
                toast.success(`${applicant.name} has been rejected.`);
                if (onDataUpdate) {
                    await onDataUpdate();
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || "Failed to reject applicant");
            }
        } catch (error) {
            console.error("Error rejecting applicant:", error);
            toast.error(error.message || "Failed to reject applicant");
        } finally {
            setIsRejecting(false);
        }
    };

    const passFailLabel = getPassFailLabel(
        selectedApplicantForAnalysis?.ai_insights?.required_questions_status
    );
    const reviewRequired = isReviewRequired(selectedApplicantForAnalysis?.ai_insights);
    const reviewReasons = getReviewReasons(selectedApplicantForAnalysis?.ai_insights);
    const overallRatingDisplay = getOverallRatingDisplay(selectedApplicantForAnalysis?.ai_insights);
    const resumeMatch = getResumeMatchDisplay(selectedApplicantForAnalysis?.ai_insights);
    return (
        <div className={isDrawer ? "mb-4" : "mt-2 mb-4"}>
            <Modal
                show={dislikeModalOpen}
                onClose={() => {
                    if (!isSavingFeedback) {
                        setDislikeModalOpen(false);
                    }
                }}
                size="lg"
            >
                <div className="p-6">
                    <Heading4 className="mb-2">Tell us more (optional)</Heading4>
                    <BodyText
                        weight="normal"
                        emphasis={false}
                        className="mb-4 block text-gray-600"
                    >
                        How can we improve this analysis? Your feedback will help us make June smarter and more useful for you.
                    </BodyText>
                    <textarea
                        value={dislikeCommentDraft}
                        onChange={(e) => setDislikeCommentDraft(e.target.value)}
                        placeholder="Optional comments…"
                        className="w-full p-3 border border-gray-300 rounded text-small min-h-[100px] mb-6"
                        disabled={isSavingFeedback}
                    />
                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setDislikeModalOpen(false)}
                            disabled={isSavingFeedback}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={confirmDislikeFeedback}
                            disabled={isSavingFeedback}
                        >
                            {isSavingFeedback ? "Saving…" : "Submit"}
                        </Button>
                    </div>
                </div>
            </Modal>

            <div className={`mb-4 flex flex-wrap items-start justify-between gap-3 pt-2 ${isDrawer ? "" : "mx-4"}`}>
                <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
                    {!isDrawer && (
                        <Button
                            variant="tertiary"
                            onClick={navigateBackToList}
                            leftIcon={<ArrowLeft size="1em" />}
                            className="w-full justify-center sm:w-auto"
                        >
                            Back to Applicants
                        </Button>
                    )}

                    <Button
                        variant="tertiary"
                        size="sm"
                        onClick={handleUpdate}
                        leftIcon={<RefreshCw size="1em" />}
                        disabled={isRetrying || isUpdating || parentIsUpdating}
                        className="w-full justify-center sm:w-auto"
                    >
                        {(isUpdating || parentIsUpdating) ? "Refreshing..." : "Refresh"}
                    </Button>
                </div>

                <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto sm:justify-end">
                    <Tooltip
                        content="Your ATS doesn't allow this feature."
                        position="left"
                        disabled={rejectSupported}
                    >
                        <span className="inline-block">
                            <Button
                                variant="danger"
                                onClick={() => setRejectModalOpen(true)}
                                disabled={selectedApplicantForAnalysis?.status === "ATS_DECLINED" || !rejectSupported}
                                className="w-full justify-center sm:w-auto"
                            >
                                Reject
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="tertiary"
                        onClick={() => copyToClipboardHandler(selectedApplicantForAnalysis, roleName, companyName)}
                        leftIcon={<Copy size="1em" />}
                        className="w-full justify-center sm:w-auto"
                        disabled={isTranscriptLoading}
                    >
                        Copy
                    </Button>
                    <Button
                        variant="tertiary"
                        onClick={async () => await exportToPDFHandler(selectedApplicantForAnalysis, roleName, companyName)}
                        leftIcon={<Download size="1em" />}
                        className="w-full justify-center sm:w-auto"
                        disabled={isTranscriptLoading}
                    >
                        Download
                    </Button>
                    <Button
                        variant="primary"
                        onClick={sendCalendarLinkFromDetail}
                        leftIcon={<Calendar size="1em" />}
                        disabled={!canSendCalendarLink || isSendingCalendarLink}
                        className="w-full justify-center sm:w-auto"
                    >
                        {calendarLinkButtonLabel}
                    </Button>
                    {selectedApplicantForAnalysis?.calendar_link_sent_at && (
                        <Button
                            variant="secondary"
                            onClick={sendReminderFromDetail}
                            leftIcon={<Bell size="1em" />}
                            disabled={!canSendCalendarLink || isSendingReminder}
                            className="w-full justify-center sm:w-auto"
                        >
                            {isSendingReminder ? "Sending..." : "Send Reminder"}
                        </Button>
                    )}
                    {companyId && jobId && token && (
                        <Dropdown
                            placement="bottom-end"
                            id={`applicant-detail-actions-${selectedApplicantForAnalysis?.id}`}
                            items={overflowMenuItems}
                            dataCyTrigger="applicant-detail-overflow-menu"
                        />
                    )}
                </div>
            </div>

            <div className={`flex flex-col gap-6 ${isDrawer ? "" : "mx-4 lg:flex-row lg:items-start"}`}>
                <CollapsibleSection
                    title={selectedApplicantForAnalysis.name}
                    contentId="applicant-overview-content"
                    dataCy="applicant-overview-section"
                    className={`h-fit w-full ${isDrawer ? "" : "lg:w-[35%]"}`}
                    actions={
                        <OutdatedQuestionsBadge
                            applicant={selectedApplicantForAnalysis}
                            companyId={companyId}
                            jobId={jobId}
                            token={token}
                            disabled={isJobClosed}
                            onResent={onDataUpdate}
                        />
                    }
                >
                    {isSafeExternalUrl(selectedApplicantForAnalysis?.ats_profile_url) && (
                        <a
                            href={selectedApplicantForAnalysis.ats_profile_url.trim()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="custom-link mb-4 inline-flex items-center gap-1.5 text-small"
                            title="Opens in your ATS (requires ATS login)"
                        >
                            View ATS profile
                            <ExternalLink className="h-3 w-3" aria-hidden />
                        </a>
                    )}
                    {(() => {
                        const { status, analysis_status, is_closed } = selectedApplicantForAnalysis || {};

                        if (status === "ABANDONED") {
                            return (
                                <div>
                                    <BodyText className="mb-4" weight="normal" emphasis={false}>
                                        No analysis available.
                                    </BodyText>
                                </div>
                            );
                        }

                        if (is_closed) {
                            const runDisabled =
                                isRetrying ||
                                !(status === "FEEDBACK" || status === "COMPLETED") ||
                                isJobClosed ||
                                !canRetryAnalysis(selectedApplicantForAnalysis, { isJobClosed, job: jobStageSettings });
                            const runReason = getRetryAnalysisDisabledReason(
                                selectedApplicantForAnalysis,
                                { isJobClosed, job: jobStageSettings }
                            );
                            return (
                                <div>
                                    <div className="-mx-4 mb-4">
                                        <Tooltip content={runReason} disabled={!runReason || !runDisabled}>
                                            <Button
                                                onClick={() => setRetryAnalysisModalOpen(true)}
                                                variant="tertiary"
                                                disabled={runDisabled}
                                                leftIcon={<RotateCw size="1em" />}
                                            >
                                                {isRetrying ? "Running..." : "Run Evaluation"}
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </div>
                            );
                        }

                        if (status === "REQUESTED_HUMAN_INTERVIEW" || status === "STOPPED_BY_APPLICANT") {
                            return (
                                <div>
                                    <BodyText className="mb-4" weight="normal" emphasis={false}>
                                        {status === "REQUESTED_HUMAN_INTERVIEW"
                                            ? "Candidate requested a human interview."
                                            : "Interview was stopped by applicant."}
                                    </BodyText>
                                </div>
                            );
                        }

                        if (analysis_status === "COMPLETED") {
                            return (
                                <div>
                                    <div className="mb-4">
                                        <BodyText weight="bold" emphasis={false} className="py-2">Overall Match Rating</BodyText>
                                        {overallRatingDisplay.kind === "rating" ? (
                                            <MatchRating rating={overallRatingDisplay.value} />
                                        ) : (
                                            <BodyText weight="normal" emphasis={false} data-testid="overall-rating-text">
                                                {overallRatingDisplay.value}
                                            </BodyText>
                                        )}
                                    </div>
                                    {reviewRequired && (
                                        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="review-required">
                                            <Badge variant="warning">Needs human review</Badge>
                                            <BodyText weight="normal" emphasis={false} className="mt-2 block text-gray-700">
                                                The analysis is complete, but a person should look at it before acting on it. This is not a rejection.
                                            </BodyText>
                                            {reviewReasons.length > 0 && (
                                                <ul className="mt-2 list-disc pl-5 text-small text-gray-700">
                                                    {reviewReasons.map((reason, i) => (
                                                        <li key={i}>{reason}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                    <div className="mb-4">
                                        <BodyText weight="bold" emphasis={false} className="py-2">Summary</BodyText>
                                        <BodyText weight="normal" emphasis={false} className="leading-[150%]">
                                            {selectedApplicantForAnalysis?.ai_insights?.overall_summary || "No summary available."}
                                        </BodyText>
                                    </div>
                                    {resumeMatch && (
                                        <div className="mb-4">
                                            <BodyText weight="bold" emphasis={false} className="py-2">Interview vs. Resume</BodyText>
                                            <BodyText weight="normal" emphasis={false} className="mb-2">
                                                How well the interview backed up the resume.
                                            </BodyText>
                                            {resumeMatch.score != null && <MatchRating rating={resumeMatch.score} />}
                                            <BodyText weight="normal" emphasis={false} className="leading-[150%] mt-1">
                                                {resumeMatch.summary}
                                            </BodyText>
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        if (shouldShowRetryButton) {
                            const retryDisabled = isRetrying || isJobClosed;
                            const retryReason = getRetryAnalysisDisabledReason(
                                selectedApplicantForAnalysis,
                                { isJobClosed, job: jobStageSettings }
                            );
                            return (
                                <ErrorBoundary>
                                    <div className="-mx-4 mb-4">
                                        <Tooltip content={retryReason} disabled={!retryReason || !retryDisabled}>
                                            <Button
                                                onClick={() => setRetryAnalysisModalOpen(true)}
                                                variant="tertiary"
                                                disabled={retryDisabled}
                                                leftIcon={<RotateCw size="1em" />}
                                            >
                                                {isRetrying ? "Retrying..." : "Re-try Evaluation"}
                                            </Button>
                                        </Tooltip>
                                    </div>
                                </ErrorBoundary>
                            );
                        }

                        const getStatusMessage = () => {
                            switch (analysis_status) {
                                case "IN_PROGRESS":
                                    return "June is evaluating the interview...";
                                case "PENDING":
                                    return "Interview is still being conducted.";
                                case "FAILED":
                                    return "Evaluation failed. Please try again later.";
                                default:
                                    return "No evaluation available.";
                            }
                        };

                        return (
                            <div>
                                <BodyText className="mb-4" weight="normal" emphasis={false}>
                                    {getStatusMessage()}
                                </BodyText>
                            </div>
                        );
                    })()}
                    <div className="mb-4 flex flex-wrap items-start gap-x-6 gap-y-3">
                        <div className="min-w-0">
                            <BodyText weight="bold" emphasis={false} className="py-2">Pass/Fail</BodyText>
                            <Badge variant={getPassFailVariant(passFailLabel)}>
                                {passFailLabel}
                            </Badge>
                        </div>
                        <div className="min-w-0">
                            <BodyText weight="bold" emphasis={false} className="py-2">Status</BodyText>
                            <Badge status={getDisplayStatus(selectedApplicantForAnalysis)}>
                                {formatStatus(getDisplayStatus(selectedApplicantForAnalysis))}
                            </Badge>
                        </div>
                        <div className="min-w-0">
                            <BodyText weight="bold" emphasis={false} className="py-2">Phone Number</BodyText>
                            <BodyText weight="normal" emphasis={false} className="leading-[150%]"> {formatPhoneNumber(selectedApplicantForAnalysis.phone)} </BodyText>
                        </div>
                        <div className="min-w-0">
                            <BodyText weight="bold" emphasis={false} className="py-2">Last Updated</BodyText>
                            <BodyText weight="normal" emphasis={false} className="leading-[150%]">
                                {selectedApplicantForAnalysis.updated_at ? new Date(selectedApplicantForAnalysis.updated_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                }) : 'N/A'}
                            </BodyText>
                        </div>
                    </div>

                    {isDevEnvironment() && devModeEnabled && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <BodyText weight="bold" emphasis={false} className="py-2">Developer mode</BodyText>
                            <BodyText weight="normal" emphasis={false} className="mb-2 block">
                                Send a test interview message as this applicant (bypasses Twilio).
                            </BodyText>
                            <textarea
                                value={devMessageBody}
                                onChange={(e) => setDevMessageBody(e.target.value)}
                                placeholder="Message text..."
                                className="w-full p-2 border border-gray-300 rounded text-small mb-2 min-h-[80px]"
                                disabled={isSendingDevMessage}
                            />
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleSendDevMessage}
                                disabled={isSendingDevMessage || !devMessageBody?.trim()}
                            >
                                {isSendingDevMessage ? "Sending..." : "Send test message"}
                            </Button>
                        </div>
                    )}
                </CollapsibleSection>

                <div className="w-full flex-1">
                    <Tabs
                        ariaLabel="Applicant detail tabs"
                        variant="underline"
                        className="z-0 applicant-analysis-tabs"
                        onTabChange={handleAnalysisTabChange}
                    >
                        <Tabs.Item title="Resume" active={initialAnalysisTab === 0}>
                            <div className="pb-4">
                                {activeAnalysisTab === RESUME_TAB_INDEX && (
                                    <ResumeTabContent
                                        candidateId={selectedApplicantForAnalysis?.id}
                                        companyId={companyId}
                                        jobId={jobId}
                                        token={token}
                                        onOpenPreview={isDrawer ? onOpenResumePreview : undefined}
                                    />
                                )}
                            </div>
                        </Tabs.Item>

                        <Tabs.Item
                            title="Evaluation Details"
                            active={initialAnalysisTab === 1}
                        >
                            <div className="pb-4">
                                {(() => {
                                    const { status, analysis_status, is_closed } = selectedApplicantForAnalysis || {};

                                    if (status === "ABANDONED") {
                                        return (
                                            <div className="p-4">
                                                <Heading6>No evaluation available.</Heading6>
                                            </div>
                                        );
                                    }

                                    if (is_closed) {
                                        return (
                                            <div className="p-4">
                                                <Heading6>Interview has been closed.</Heading6>
                                            </div>
                                        );
                                    }

                                    if (status === "REQUESTED_HUMAN_INTERVIEW" || status === "STOPPED_BY_APPLICANT") {
                                        return (
                                            <div className="p-4">
                                                <Heading6>
                                                    {status === "REQUESTED_HUMAN_INTERVIEW"
                                                        ? "Candidate requested a human interview."
                                                        : "Interview was stopped by applicant."}
                                                </Heading6>
                                            </div>
                                        );
                                    }

                                    switch (analysis_status) {
                                        case "PENDING":
                                            return (
                                                <div className="p-4">
                                                    <Heading6>Interview is still being conducted.</Heading6>
                                                </div>
                                            );

                                        case "IN_PROGRESS":
                                            return (
                                                <div className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <Heading6>June is evaluating the interview...</Heading6>
                                                        <Spinner size="md" />
                                                    </div>
                                                </div>
                                            );

                                        case "FAILED":
                                            return (
                                                <div className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <Heading6>Evaluation failed</Heading6>
                                                    </div>
                                                </div>
                                            );

                                        case "COMPLETED":
                                            return (
                                                <div className="space-y-8">
                                                    {selectedApplicantForAnalysis?.ai_insights?.questions_evaluation && (
                                                        <div>
                                                            <div className="space-y-6">
                                                                {selectedApplicantForAnalysis.ai_insights.questions_evaluation.map(
                                                                    (question, idx) => (
                                                                        <QuestionCard
                                                                            key={idx}
                                                                            question={question}
                                                                            index={idx}
                                                                            isExpanded={expandedQuestions.has(idx)}
                                                                            onToggle={toggleQuestion}
                                                                            aiInsights={selectedApplicantForAnalysis?.ai_insights}
                                                                        />
                                                                    )
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <div className="flex gap-2">
                                                            <BodyText weight="bold" emphasis={false} className="py-2 block">Was this evaluation helpful?</BodyText>
                                                            <Button
                                                                variant={isAnalysisLiked === true ? "tertiary" : "secondary"}
                                                                size="sm"
                                                                onClick={() => handleAnalysisLike(true)}
                                                                disabled={isSavingFeedback}
                                                            >
                                                                <ThumbsUp size="1em" className="inline" aria-hidden />
                                                            </Button>
                                                            <Button
                                                                variant={isAnalysisLiked === false ? "danger" : "secondary"}
                                                                size="sm"
                                                                onClick={() => handleAnalysisLike(false)}
                                                                disabled={isSavingFeedback}
                                                            >
                                                                <ThumbsDown size="1em" className="inline" aria-hidden />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );

                                        default:
                                            return (
                                                <div className="p-4">
                                                    <Heading6>No evaluation available.</Heading6>
                                                </div>
                                            );
                                    }
                                })()}
                            </div>
                        </Tabs.Item>

                        <Tabs.Item title="Transcript" active={initialAnalysisTab === 2}>
                            <div className="pb-4">
                                {isTranscriptLoading ? (
                                    <div className="p-4 flex items-center gap-3">
                                        <Heading6>Loading transcript...</Heading6>
                                        <Spinner size="md" />
                                    </div>
                                ) : transcriptError ? (
                                    <div className="p-4 space-y-3">
                                        <Heading6>{transcriptError}</Heading6>
                                        {onRetryTranscript && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={onRetryTranscript}
                                            >
                                                Retry
                                            </Button>
                                        )}
                                    </div>
                                ) : (selectedApplicantForAnalysis?.interview_responses || []).length === 0 ? (
                                    <div className="p-4">
                                        <Heading6>No transcript available.</Heading6>
                                    </div>
                                ) : (
                                <div className="space-y-6">
                                    {selectedApplicantForAnalysis?.interview_responses
                                        ?.slice()
                                        .sort((a, b) => a.question_number - b.question_number)
                                        .map((item) => (
                                            <div key={item.id || item.question_number} className="bg-white p-4 rounded-lg border border-gray-200">
                                                <BodyText weight="bold" emphasis={false} className="mb-4">
                                                    June: {item.question}
                                                </BodyText>
                                                <BodyText weight="normal" emphasis={false} className="mb-2">
                                                    Applicant: {item.response || "No response provided."}
                                                </BodyText>
                                            </div>
                                        ))}
                                </div>
                                )}
                            </div>
                        </Tabs.Item>
                    </Tabs>


                </div>
            </div>

            <MultipleApplicationsModal
                show={applicationsModalOpen}
                onClose={() => setApplicationsModalOpen(false)}
                applicant={selectedApplicantForAnalysis}
            />

            <RejectApplicantModal
                isOpen={rejectModalOpen}
                onClose={() => setRejectModalOpen(false)}
                onConfirm={handleRejectConfirm}
                count={1}
                applicantName={selectedApplicantForAnalysis?.name}
                companyId={companyId}
                token={token}
                isLoading={isRejecting}
            />

            <ConfirmationModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDeleteApplicant}
                {...getModalConfig(
                    "deleteApplicant",
                    1,
                    "Applicant",
                    selectedApplicantForAnalysis?.name
                )}
                confirmButtonVariant="danger"
                isLoading={isActionLoading}
                loadingText="Deleting..."
            />

            <ConfirmationModal
                isOpen={resendInterviewModalOpen}
                onClose={() => setResendInterviewModalOpen(false)}
                onConfirm={confirmResendInterview}
                {...getModalConfig(
                    "resend",
                    1,
                    "Interview",
                    selectedApplicantForAnalysis?.name
                )}
                confirmButtonVariant="danger"
                isLoading={isActionLoading}
                loadingText="Resending..."
            />

            <AnalysisRubricRequiredModal
                isOpen={analysisRubricWarningOpen}
                onClose={() => setAnalysisRubricWarningOpen(false)}
                onConfirm={() => {
                    setAnalysisRubricWarningOpen(false);
                    navigate(
                        `/jobs/${encodeURIComponent(roleName || "Role")}/${jobId}?view=analysis`
                    );
                }}
            />

            <ConfirmationModal
                isOpen={retryAnalysisModalOpen}
                onClose={() => setRetryAnalysisModalOpen(false)}
                onConfirm={() => retryAnalysisHandler(false)}
                onSecondaryAction={() => retryAnalysisHandler(true)}
                secondaryActionVariant="secondary"
                secondaryActionDisabled={!canRetryMoveAtsStages(jobStageSettings)}
                secondaryActionDisabledReason={getAtsStageUpdateDisabledReason(jobStageSettings)}
                cancelButtonVariant="ghost"
                {...getModalConfig(
                    "retryAnalysis",
                    1,
                    "Applicant",
                    selectedApplicantForAnalysis?.name
                )}
                confirmButtonVariant="primary"
            />

            <ConfirmationModal
                isOpen={interviewActionModalOpen}
                onClose={() => setInterviewActionModalOpen(false)}
                onConfirm={confirmInterviewFollowUpAction}
                {...getModalConfig(
                    selectedApplicantForAnalysis?.is_closed ? "reopen" : "close",
                    1,
                    "Interview",
                    selectedApplicantForAnalysis?.name
                )}
                confirmButtonVariant={
                    selectedApplicantForAnalysis?.is_closed ? "tertiary" : "danger"
                }
                isLoading={isActionLoading}
                loadingText={
                    selectedApplicantForAnalysis?.is_closed
                        ? "Enabling..."
                        : "Disabling..."
                }
            />
        </div>
    );
};

export default ApplicantAnalysisResult;
